import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quotes, CaretLeft, CaretRight } from "@phosphor-icons/react";

const testimonials = [
    {
        name: "Catherine Lemoine",
        role: "Propriétaire · Maison · Paris 15ème",
        rating: 5,
        text: "Audit réalisé en moins de 48h, rapport ultra clair. L'expert LUMOS a pris le temps d'expliquer chaque recommandation. Très professionnel.",
        initials: "CL",
        color: "#00AFFF",
    },
    {
        name: "Jean-Marc Verdier",
        role: "Syndic bénévole · Copropriété · Compiègne",
        rating: 5,
        text: "Nous cherchions un bureau d'étude réactif pour notre DPE collectif. Mission impeccable, rapport conforme, équipe à l'écoute du conseil syndical.",
        initials: "JV",
        color: "#22C55E",
    },
    {
        name: "Sophie Renault",
        role: "Gestionnaire immeuble · Saint-Quentin",
        rating: 5,
        text: "Le PPPT a été livré dans les délais avec un plan d'action chiffré sur 10 ans. Un vrai accompagnement, pas juste un document technique.",
        initials: "SR",
        color: "#F59E0B",
    },
    {
        name: "Olivier Marchand",
        role: "Propriétaire · Pavillon · Versailles",
        rating: 5,
        text: "Prix fixe annoncé, prix fixe respecté. Aucune surprise, des conseils concrets pour ma rénovation. Je recommande vivement.",
        initials: "OM",
        color: "#00AFFF",
    },
    {
        name: "Isabelle Brunet",
        role: "Présidente CS · Copropriété · Beauvais",
        rating: 5,
        text: "L'audit énergétique a permis de débloquer notre vote en AG. Rapport pédagogique et compréhensible par tous les copropriétaires.",
        initials: "IB",
        color: "#22C55E",
    },
];

export default function Testimonials() {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setIndex((i) => (i + 1) % testimonials.length), 6000);
        return () => clearInterval(id);
    }, []);

    const t = testimonials[index];

    return (
        <section data-testid="testimonials-section" className="relative py-24 lg:py-28 bg-white">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
                <div className="text-center mb-12">
                    <span className="eyebrow justify-center">Ils nous font confiance</span>
                    <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-lumos-ink leading-[1.05]">
                        Des clients<br />
                        <span className="font-serif-display text-lumos-blue">qui en parlent mieux que nous.</span>
                    </h2>
                </div>

                <div className="relative">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -22 }}
                            transition={{ duration: 0.5 }}
                            className="relative rounded-3xl bg-lumos-bg border border-lumos-border p-9 lg:p-14 overflow-hidden"
                            data-testid="testimonial-card"
                        >
                            <Quotes size={64} weight="fill" className="absolute top-7 right-7 text-lumos-blue/8" />

                            <div className="flex items-center gap-1 mb-6">
                                {Array.from({ length: t.rating }).map((_, i) => (
                                    <Star key={i} size={20} weight="fill" className="text-lumos-amber" />
                                ))}
                            </div>

                            <blockquote className="font-display text-xl lg:text-2xl xl:text-3xl text-lumos-ink font-medium leading-relaxed max-w-3xl">
                                « {t.text} »
                            </blockquote>

                            <div className="mt-8 flex items-center gap-4">
                                <div
                                    className="w-12 h-12 rounded-full grid place-items-center text-white font-display font-bold text-sm"
                                    style={{ background: t.color }}
                                >
                                    {t.initials}
                                </div>
                                <div>
                                    <div className="font-display font-bold text-lumos-ink">{t.name}</div>
                                    <div className="text-sm text-lumos-muted">{t.role}</div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {/* Controls */}
                    <div className="flex items-center justify-between mt-7">
                        <div className="flex gap-2">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setIndex(i)}
                                    data-testid={`testimonial-dot-${i}`}
                                    aria-label={`Témoignage ${i + 1}`}
                                    className={`h-2 rounded-full transition-all duration-400 ${
                                        i === index ? "w-10 bg-lumos-blue" : "w-2 bg-lumos-border"
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setIndex((i) => (i - 1 + testimonials.length) % testimonials.length)}
                                data-testid="testimonial-prev"
                                aria-label="Témoignage précédent"
                                className="w-11 h-11 rounded-full border border-lumos-border bg-white hover:border-lumos-blue hover:text-lumos-blue text-lumos-ink grid place-items-center transition-all"
                            >
                                <CaretLeft size={18} weight="bold" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setIndex((i) => (i + 1) % testimonials.length)}
                                data-testid="testimonial-next"
                                aria-label="Témoignage suivant"
                                className="w-11 h-11 rounded-full border border-lumos-border bg-white hover:border-lumos-blue hover:text-lumos-blue text-lumos-ink grid place-items-center transition-all"
                            >
                                <CaretRight size={18} weight="bold" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
